import React, { useEffect, useContext, useState, useRef } from 'react';
import {
    Grid,
    Table,
    PagingPanel,
    TableFilterRow,
    TableRowDetail,
    TableHeaderRow,
    TableBandHeader,
    TableColumnVisibility,
} from '@devexpress/dx-react-grid-bootstrap4';
import { FilteringState, PagingState, CustomPaging, RowDetailState, SortingState, SelectionState } from '@devexpress/dx-react-grid';
import moment from 'moment';
import qs from 'query-string';
import axios from 'axios';

import { FilterColumns, ChangeTablePage, HandlePageLoading } from '../shared/TableEventHelpers';
import { CoveringFilterOperations } from '../shared/FilterOperations';
import NumberOfSearchResults from '../shared/NumberOfSearchResults';
import ConvertTableToExcel from '../shared/ConvertTableToExcel';
import TableHeaderCell from '../shared/TableHeaderCell';
import FilterResetter from '../shared/FilterResetter';
import ColumnChooser from '../shared/ColumnChooser';
import TableFooter from '../shared/TableFooter';
import NoDataRow from '../shared/NoDataRow';
import TableRow from '../shared/TableRow';

import { FormatColumnsCovering, EstablishColumnBands, IsWeekSpanLessThanOccupancyCheck } from './CoveringHelperFunctions';
import DateTimeInputCoverings from './DateTimeInputCoverings';
import TableHeaderCovering from './TableHeaderCovering';
import DescriptionFilters from './description_filters/DescriptionFilters';
import CustomDatePicker from './CustomDatePicker';
import SelectOccupancy from './SelectOccupancy';
import CoveringToasts from './CoveringToasts';
import WeekPickers from './WeekPickers';
import FilterCell from './FilterCell';
import RowDetail from './RowDetail';
import TableCell from './TableCell';

import { DataContext } from '../../../stores/covering-table-store';
import { DataContext as MainStoreContext } from '../../../store';
import Loading from '../../Loading';

import { PagingShowAllText } from '../../../functions/TableFunctions';
import WeekHelper from '../../../functions/WeekHelper';
import { useTranslation } from 'react-i18next';
import { A } from 'hookrouter';

const CancelToken = axios.CancelToken;
let cancel;

const CancelToken2 = axios.CancelToken;
let cancel2;

const CoveringTable = () => {
    const { store, dispatch } = useContext(DataContext);
    const mainStore = useContext(MainStoreContext);
    const { t } = useTranslation(['coveringTable']);

    const {
        isLoading,
        tableSize,
        columnName,
        filters,
        descriptionFilters,
        filterByBossEmpId,
        requestUri,
        direction,
        pageSize,
        error,
        coverings,
        columns,
        noWeeksColumns,
        hiddenColumns,
        defaultFilters,
        showCustomCols,
        currentPage,
        expandedRowIDs,
        columnSortingExtensions,
        columnBands,
        weekWidth,
        availableDates,
        latestDate,
        selectedDate,
        selectedOccupancy,
        startWeek,
        endWeek,
        tableColumnExtensions,
        defaultTableColumnExtensions,
    } = store;

    function translateColumnTitles() {
        columns.map((col) => {
            col.title = t(col.title);
        })
    }

    const user = mainStore?.store?.user;
    let params = {
        filters: JSON.stringify(filters),
        descriptionFilters: JSON.stringify(descriptionFilters),
        sortColumn: columnName,
        sortDirection: direction,
        currentPage: currentPage,
        pageSize: pageSize,
        startWeek: WeekHelper.GetWeek(startWeek),
        endWeek: WeekHelper.GetWeek(endWeek),
        coveringDate: moment(selectedDate).format('YYYY-MM-DD').toString(),
        occupancyCheck: selectedOccupancy.value,
        filterByBossEmpId: filterByBossEmpId,
    };

    const paramsRef = useRef(params);
    const [hasChanged, setHasChanged] = useState(false);
    const [initialCoveringDate, setInitialCoveringDate] = useState(new Date());

    const initialParams = {
        ...paramsRef.current,
        coveringDate: moment(initialCoveringDate).format('YYYY-MM-DD').toString(),
    };

    useEffect(() => {
        async function fetchData() {
            cancel && cancel();
            const uri = `api/covering/`;

            // push to url if its not initial or changed
            if (JSON.stringify(params) !== JSON.stringify(initialParams) || hasChanged) {
                const queryString = qs.stringify(params);
                const url = `${window.location.pathname}?${queryString}`;
                window.history.pushState(null, '', url);
            } else {
                setHasChanged(true);
            }

            const newRequestUri = uri + '/' + JSON.stringify(params);

            if (newRequestUri === requestUri && mainStore.store.loadedTabs['/coverings']) {
                return;
            }
            dispatch({ type: 'SET_ISLOADING_TRUE' });
            dispatch({ type: 'SET_REQUEST_URI', payload: newRequestUri });

            try {
                axios
                    .get(uri, {
                        params,
                        cancelToken: new CancelToken((c) => {
                            cancel = c;
                        }),
                        headers: {
                            Accept: 'application/json, text/plain, */*',
                            'Content-Type': 'application/json;charset=UTF-8',
                        },
                    })
                    .then((response) => {
                        const { rows, totalRows, columns, descriptionSearchSuggestions, filterByBossEmpId } = response.data;

                        const formattedColumns = FormatColumnsCovering(columns, tableColumnExtensions, weekWidth);

                        const colSortExts = formattedColumns.map((column) => {
                            return { columnName: column.name, sortingEnabled: false };
                        });
                        const colBands = EstablishColumnBands(startWeek, endWeek);

                        dispatch({
                            type: 'FETCH_SUCCESS',
                            availableDates,
                            columns: formattedColumns,
                            columnSortingExtensions: colSortExts,
                            coverings: rows,
                            columnBands: colBands,
                            totalRows,
                            descriptionSearchSuggestions,
                            filterByBossEmpId,
                        });
                    })
                    .catch((thrown) => {
                        if (axios.isCancel(thrown)) {
                            //TODO Something when request is cancelled?
                        } else {
                            const error = thrown.response;
                            dispatch({ type: 'FETCH_ERROR', payload: error });
                        }
                    });
            } catch (error) {
                console.error(error);
                dispatch({ type: 'SET_ISLOADING_FALSE' });
            }
        }

        if (!IsWeekSpanLessThanOccupancyCheck(selectedOccupancy.value, startWeek, endWeek, dispatch)) {
            if (latestDate) {
                fetchData();
            }
        }
        translateColumnTitles();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnName, currentPage, filters, descriptionFilters, direction, pageSize, startWeek, endWeek, selectedDate, selectedOccupancy]);

    useEffect(() => {
        const handleHiddenColumns = () => {
            const hidColumns = FilterColumns(noWeeksColumns, hiddenColumns, showCustomCols, defaultTableColumnExtensions, true, dispatch);
            if (hidColumns !== null) {
                dispatch({ type: 'HIDDEN_COLUMNS', payload: hidColumns });
            }
        };

        handleHiddenColumns();
        window.addEventListener('resize', handleHiddenColumns);

        return function cleanup() {
            window.removeEventListener('resize', handleHiddenColumns);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns, noWeeksColumns]);

    useEffect(() => {
        HandlePageLoading(isLoading);
    }, [isLoading]);

    useEffect(() => {
        async function fetchInitialData() {
            const url = `api/covering/initialdata`;
            cancel2 && cancel2();

            try {
                axios
                    .get(url, {
                        params: {},
                        cancelToken: new CancelToken2((c) => {
                            cancel2 = c;
                        }),
                        headers: {
                            Accept: 'application/json, text/plain, */*',
                            'Content-Type': 'application/json;charset=UTF-8',
                        },
                    })
                    .then((response) => {
                        const availableDates = response.data.dates.map((date) => moment(date.substring(0, 10), 'YYYY-MM-DD').toDate());
                        const lastElem = availableDates[0];
                        setInitialCoveringDate(lastElem);

                        dispatch({
                            type: 'FETCH_DATA_SUCCESS',
                            availableDates,
                            selectedDate: lastElem,
                            latestDate: lastElem,
                            BltIDNames: response.data.blts,
                        });
                    })
                    .catch((thrown) => {
                        if (axios.isCancel(thrown)) {
                            //TODO Something when request is cancelled?
                        } else {
                            const error = thrown.response;
                            dispatch({ type: 'FETCH_DATES_ERROR', payload: error });
                        }
                    });
            } catch (error) {
                console.error(error);
            }
        }

        if (!(availableDates.length > 0)) {
            fetchInitialData(t);
        }
        PagingShowAllText(t);
        const nameFilterInput = document.querySelector('#empName-filter .form-control');
        nameFilterInput && nameFilterInput.focus();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSortChange = (sorting) => {
        dispatch({
            type: 'SORT_INFO',
            columnName: sorting[0].columnName,
            direction: sorting[0].direction,
        });
    };

    const handlePageSizeChange = (pageSize) => {
        ChangeTablePage(pageSize, dispatch, currentPage, tableSize);
    };

    const handleDateChange = (cDate) => {
        dispatch({
            type: 'DATE_UPDATE',
            selectedDate: cDate,
        });
    };
    return (
        <>
            <Grid columns={columns} rows={coverings} className='padding-right-60px'>
                <PagingState
                    pageSize={pageSize}
                    currentPage={currentPage - 1}
                    onPageSizeChange={handlePageSizeChange}
                    onCurrentPageChange={(currentPage) => {
                        dispatch({ type: 'CURRENT_PAGE', payload: currentPage + 1 });
                        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                    }}
                />
                <SortingState
                    onSortingChange={handleSortChange}
                    defaultSorting={[{ columnName: 'empName', direction: 'asc' }]}
                    columnExtensions={columnSortingExtensions}
                />
                <CustomPaging totalCount={tableSize} />

                <SelectionState />

                <FilteringState defaultFilters={defaultFilters} />
                <RowDetailState
                    expandedRowIds={expandedRowIDs}
                    onExpandedRowIdsChange={(ids) => {
                        dispatch({ type: 'UPDATE_ROW_DETAILS', payload: ids });
                    }}
                />

                <Table
                    columnExtensions={tableColumnExtensions}
                    cellComponent={TableCell}
                    rowComponent={(props) => TableRow(props, 'covering')}
                    noDataRowComponent={(props) => NoDataRow(isLoading, error, props)}
                />

                <CoveringFilterOperations />
                
                <TableRowDetail contentComponent={RowDetail} cellComponent={RowDetailCell} />
                <TableFilterRow showFilterSelector cellComponent={FilterCell} />

                <TableHeaderRow showSortingControls cellComponent={TableHeaderCell} />
                <TableBandHeader columnBands={columnBands} />
                <PagingPanel pageSizes={[10, 25, 50, 10000]}  messages={PagingShowAllText}/>

                <TableColumnVisibility hiddenColumnNames={hiddenColumns} />

                <TableHeaderCovering tableSize={tableSize}>
                    <>
                        {filterByBossEmpId ? (
                            <div className='d-flex justify-content-between align-items-center'>
                                <span class="font-italic">
                                    {filterByBossEmpId.toUpperCase() === user?.id?.toUpperCase() ?
                                        (<>
                                            Filtering by: 
                                            <A href={`/employee/${user.id}/info`}>
                                                {user.name}
                                            </A>
                                        </>) :
                                        (<>
                                            Filtering by Boss: 
                                            <A href={`/employee/${filterByBossEmpId}/info`}>
                                                {filterByBossEmpId.toUpperCase()}
                                            </A>
                                        </>)
                                    }
                                </span>
                            </div>
                        ) : (<></>)}
                        <CustomDatePicker
                            lastDate={latestDate}
                            avDates={availableDates}
                            customInput={<DateTimeInputCoverings />}
                            onChange={(date) => handleDateChange(date)}
                            popperPlacement='bottom-end'
                            showPopperArrow={false}
                        />
                        {tableSize > 0 ? (
                            <ConvertTableToExcel
                                filters={filters}
                                columns={columns}
                                descriptionFilters={descriptionFilters}
                                hiddenColumns={hiddenColumns}
                                table='covering'
                                optionalParams={{
                                    SortColumn: columnName,
                                    SortDirection: direction,
                                    StartWeek: WeekHelper.GetWeek(startWeek),
                                    EndWeek: WeekHelper.GetWeek(endWeek),
                                    CoveringDate: moment(selectedDate).format('YYYY-MM-DD').toString(),
                                    OccupancyCheck: selectedOccupancy.value,
                                }}
                            />
                        ) : null}
                        <ColumnChooser
                            hiddenColumns={hiddenColumns}
                            columns={noWeeksColumns}
                            onHiddenColumnChange={(hiddenColumns) => {
                                dispatch({
                                    type: 'HIDDEN_COLUMNS_AND_CUSTOM_COLS',
                                    hiddenColumns,
                                    showCustomCols: true,
                                });
                            }}
                            onReset={() => {
                                dispatch({ type: 'RESET_HIDDEN_COLUMNS' });
                            }}
                        />
                        <FilterResetter DataContext={DataContext} />
                    </>
                    <SelectOccupancy selectedValue={params.occupancyCheck} />
                    <DescriptionFilters />
                    <WeekPickers className='week-pickers' />
                </TableHeaderCovering>

                {isLoading ? <Loading /> : null}

                <TableFooter>
                    <NumberOfSearchResults pageSize={pageSize} tableSize={tableSize} currentPage={currentPage} t={t} />
                </TableFooter>
            </Grid>
            <CoveringToasts />
        </>
    );
};

export default CoveringTable;

const RowDetailCell = ({ ...restProps }) => {
    return <TableRowDetail.Cell {...restProps} className='bg-rowdetail' />;
};
